import {isString} from 'lodash';
import React, {FC, ReactNode, useRef} from 'react';
import styled from 'styled-components';

import {
  FrontMouseEvent,
  Handler,
  HandlerOf,
} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {DropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {SimpleDropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontSizes} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {TooltipIconWithHover} from '../../../../../analytics/components/reportElements/tooltips/analyticsTooltips';
import {useAppSelector} from '../../../../../helpers/reduxHelpers';
import {InboxesSearchLocationsEnum} from '../../../../../models/url/models/inboxesUrlModel';
import {getHasPreventPinTeamTags} from '../../../../../store/featureFlags/featureFlagsSelectors';
import {
  CellsHeaderFiltersProps,
  filterTooltips,
  filterTooltipsPreventPinTeamTags,
  renderFilterIcon,
  renderFilterName,
} from '../../../cells/header/cellsHeaderFilters';
import {
  computeInboxesSearchBarButtonBackgroundColor,
  InboxesSearchBarButtonStyleProps,
} from '../queryBuilder/inboxesSearchQueryBuilderButton';

/*
 * Props.
 */

interface InboxesSearchFiltersProps {
  isOpen: boolean;
  onToggleMouseDown: HandlerOf<FrontMouseEvent>;
  onToggleClick: Handler;
  isDisabled: boolean;
  hasFocus: boolean;
  searchFilters: CellsHeaderFiltersProps<InboxesSearchLocationsEnum>;
  selectedFilter: InboxesSearchLocationsEnum;
  onSearchFilterSelect: HandlerOf<InboxesSearchLocationsEnum>;
}

/*
 * Styles.
 */

const StyledFiltersButton = styled(ChromelessButton)<InboxesSearchBarButtonStyleProps>`
  display: flex;

  ${(p) => computeInboxesSearchBarButtonBackgroundColor(p.$hasFocus, p.$isOpen)}
`;

const StyledFiltersContent = styled.div<{$hasFocus: boolean}>`
  display: flex;
  align-items: center;
  justify-content: center;

  margin: 5px 0;
  padding: 0 8px;

  border-right: 1px solid ${(p) => (p.$hasFocus ? p.theme.greys.shade30 : p.theme.alphas.black40)};
`;

const StyledFilterNameDiv = styled.div<{$hasFocus: boolean}>`
  margin-right: 8px;
  font-size: ${fontSizes.medium};
  color: ${(p) => (p.$hasFocus ? p.theme.greys.shade90 : p.theme.greys.white)};
  white-space: nowrap;
`;

const StyledDropdownRowDiv = styled.div`
  ${flexCenter}
`;

const StyledIconWrapperDiv = styled.div`
  grid-area: left-icon;

  width: 16px;
  margin-right: 8px;

  align-self: center;
  display: flex;
  justify-content: center;
`;

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  margin-left: 4px;
`;

/*
 * Component.
 */

export const InboxesSearchFilters: FC<InboxesSearchFiltersProps> = ({
  isOpen,
  onToggleMouseDown,
  onToggleClick,
  isDisabled,
  hasFocus,
  searchFilters,
  selectedFilter,
  onSearchFilterSelect,
}) => {
  const theme = useUserTheme();
  const hasPreventPinTeamTags = useAppSelector(getHasPreventPinTeamTags);

  const {localSearchFilterName, localSearchFilterIcon, filters} = searchFilters;

  const buttonRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <StyledFiltersButton
        $hasFocus={hasFocus}
        $isOpen={isOpen}
        ref={buttonRef}
        hasHover={isOpen}
        isOpen={isOpen}
        isDisabled={isDisabled}
        onMouseDown={onToggleMouseDown}
        onClick={onToggleClick}
      >
        <StyledFiltersContent $hasFocus={hasFocus}>
          <StyledFilterNameDiv $hasFocus={hasFocus}>
            {maybeTruncate(renderFilterName(selectedFilter, localSearchFilterName))}
          </StyledFilterNameDiv>
          <Icon name="chevronDownSmall" color={hasFocus ? theme.greys.shade70 : theme.greys.white} />
        </StyledFiltersContent>
      </StyledFiltersButton>
      {isOpen && (
        <SimpleDropdown anchor={buttonRef} onRequestClose={onToggleClick}>
          {filters.map((filter) => (
            <DropdownItem
              key={filter}
              isChecked={selectedFilter === filter}
              onSelect={() => onSearchFilterSelect(filter)}
              shouldReserveRightIcon
            >
              {localSearchFilterIcon && (
                <StyledIconWrapperDiv>{renderFilterIcon(filter, localSearchFilterIcon)}</StyledIconWrapperDiv>
              )}
              <DropdownItemContent isChecked={selectedFilter === filter}>
                <StyledDropdownRowDiv>
                  {renderFilterName(filter, localSearchFilterName)}
                  {maybeRenderTooltip(filter, hasPreventPinTeamTags)}
                </StyledDropdownRowDiv>
              </DropdownItemContent>
            </DropdownItem>
          ))}
        </SimpleDropdown>
      )}
    </>
  );
};

/*
 * Helpers.
 */

function maybeTruncate(filterName: ReactNode) {
  if (!filterName) {
    return null;
  }

  if (!isString(filterName)) {
    return filterName;
  }

  const shortName = filterName.length > 14 ? `${filterName.slice(0, 14)}…` : filterName;
  return shortName;
}

function maybeRenderTooltip(filter: InboxesSearchLocationsEnum, hasPreventPinTeamTags: boolean) {
  const tooltip = hasPreventPinTeamTags ? filterTooltipsPreventPinTeamTags[filter] : filterTooltips[filter];

  if (!tooltip) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledTooltipCoordinator render={() => tooltip} maxWidth={240}>
          <TooltipIconWithHover color={theme.greys.shade70} />
        </StyledTooltipCoordinator>
      )}
    </UserThemeConsumer>
  );
}
